import React, { useEffect, useRef, useState } from 'react';
import './CategoryMultiSelect.css';

interface CategoryOption {
  id: number;
  name: string;
}

interface CategoryMultiSelectProps {
  id?: string;
  categories: CategoryOption[];
  selectedIds: number[];
  onChange: (ids: number[]) => void;
  placeholder?: string;
}

// Dropdown that stays open while ticking categories; closes on outside click / Esc.
const CategoryMultiSelect: React.FC<CategoryMultiSelectProps> = ({
  id,
  categories,
  selectedIds,
  onChange,
  placeholder = 'Select Categories',
}) => {
  const [open, setOpen] = useState(false);
  const rootRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!open) return;
    const handlePointerDown = (e: PointerEvent) => {
      if (rootRef.current && !rootRef.current.contains(e.target as Node)) setOpen(false);
    };
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setOpen(false);
    };
    document.addEventListener('pointerdown', handlePointerDown);
    document.addEventListener('keydown', handleKeyDown);
    return () => {
      document.removeEventListener('pointerdown', handlePointerDown);
      document.removeEventListener('keydown', handleKeyDown);
    };
  }, [open]);

  const toggle = (categoryId: number) => {
    onChange(
      selectedIds.includes(categoryId)
        ? selectedIds.filter(x => x !== categoryId)
        : [...selectedIds, categoryId]
    );
  };

  const selectedNames = categories.filter(c => selectedIds.includes(c.id)).map(c => c.name);

  return (
    <div className="category-multiselect" ref={rootRef}>
      <button
        type="button"
        id={id}
        className={`category-multiselect-trigger ${open ? 'open' : ''}`}
        onClick={() => setOpen(o => !o)}
        aria-haspopup="listbox"
        aria-expanded={open}
      >
        <span className={selectedNames.length ? '' : 'category-multiselect-placeholder'}>
          {selectedNames.length ? selectedNames.join(', ') : placeholder}
        </span>
        <span className="category-multiselect-arrow">▾</span>
      </button>

      {open && (
        <ul className="category-multiselect-menu" role="listbox" aria-multiselectable="true">
          {categories.map(category => {
            const checked = selectedIds.includes(category.id);
            return (
              <li
                key={category.id}
                role="option"
                aria-selected={checked}
                className={`category-multiselect-option ${checked ? 'selected' : ''}`}
                onClick={() => toggle(category.id)}
              >
                <span className="category-multiselect-check">{checked ? '✓' : ''}</span>
                {category.name}
              </li>
            );
          })}
        </ul>
      )}
    </div>
  );
};

export default CategoryMultiSelect;
